// =============================================================================
// BottomNav (WS-5) — persistent bottom tab bar for the operator's hot screens.
//
// Why: the shell is a deliberate hub-and-spoke (dashboard tiles → page → back
// to dashboard). Switching between two sibling tasks in a shift — QC ↔ Pallets
// ↔ Dispatch on the floor, Scouting ↔ Sprays in the orchard — was a 3-tap home
// round-trip every time. This bar cuts that to one thumb tap. It SUPPLEMENTS the
// dashboard hub (Home is always the first tab); it does not replace it.
//
// Scope (decided 2026-06-29):
//   • BigPack (PACKHOUSE): Home · Tipping · Pallets · QC · Dispatch, and
//     Inspection in a FREE slot (F03 UF-03, 2026-09-30): an inspector's or a QC
//     technician's bar was Home · Pallets · QC — the page they spend the day on
//     was not on it. A bar whose five slots are full keeps them.
//     Per role (decided 21 Oct 2026, UX-laws audit — serial position + Fitts's
//     law: floor roles reached <=20% of their default modules from the bar):
//     SCALE_OPERATOR Tipping · Deliveries · Runs · Pallets; LOADER Dispatch ·
//     Floor Map · Coldroom · Pallets; DISPATCHER Dispatch · Consignments ·
//     Floor Map · Pallets; QC_TECHNICIAN QC · Pallets · Runs · Inspection;
//     INSPECTOR Inspection · QC · Pallets · Consignments. Home is first.
//     Every other role (ADMIN, MANAGER, ...) keeps the default bar above. The
//     role is `me.role` — the active tenant's role overlay, the one hasModule
//     gates routes with. A tab the viewer cannot open is dropped as always; the
//     free slots then take the default tabs the role lacks (packTabsForRole).
//   • BigFarm  (FARM):     Home · Scouting · Sprays · Picks
//   • BigLogistics (TRANSPORTER): Home · Trips · Availability · Fleet — added
//     2026-10-02 (ui-flow T1 UF-14). A carrier's dispatcher works from a phone
//     and switched between those three pages through Home every time.
//   • BigExport + every other kind: no bar (desk-first; gets the WS-7 read-lane).
//
// Visibility:
//   • Touch only — `hidden coarse:flex` (the Wave-0 `coarse:` = @media pointer:
//     coarse). Mouse/desktop keeps the hub; floor tablets + orchard phones get
//     the bar in any orientation.
//   • Tabs are filtered by `hasModule` (the same gate the routes use) so a tab
//     can never lead to a denied route; if only Home survives, the bar hides.
//   • Never on a full-screen surface that is not an app page (NO_BAR_ROUTES):
//     the face-clock kiosk, opened from Home while signed in, kept the bar —
//     it covered the Live strip and the lower edge of "Use PIN instead" on a
//     landscape tablet, and put one-tap exits into the signed-in account
//     beside the kiosk's own Back, which is the exit that turns the camera
//     off (farm audit HV-20). The floor monitor too: a wall display the bar
//     covered, even in Fullscreen, and it gave every passer-by a one-tap exit
//     out of the wall view (packhouse audit PM-PLN-23).
//
// Layout coexistence: the bar is `fixed bottom-0`. It toggles a `has-bottom-nav`
// body class; index.css reserves matching bottom padding ON THE BODY (under the
// same pointer:coarse query) so the fixed bar never covers page content or the
// WS-4 sticky action footers — the h-full chain shrinks #root by that padding.
// A page that GROWS past #root scrolls the window beyond that padding, so the
// bar's height is also reserved on every page root in RailShell's `page-slot`
// (index.css §Bottom-nav — the long comments there record the wrong turns).
// The OfflineQueueBadge lifts above the bar via the same body class. Dialogs
// (z-dialog) and modals (z-modal) sit above the bar (z-chrome), so they cover it.
// =============================================================================

import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react'
import { useLocation } from 'react-router-dom'
import GuardedNavLink from '@/components/GuardedNavLink'
import { Home, Layers, Package, Archive, FlaskConical, Send, Truck, Snowflake, Grid3x3, ShieldCheck, Bug, SprayCan, Sprout, CalendarDays, Car, Shield, MapPin, AlertTriangle } from 'lucide-react'
import clsx from 'clsx'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { useActiveTenant, type ActiveTenant, type TenantKind } from '@/lib/useActiveTenant'
import { hasModule } from '@/lib/modules'
import type { UserRole } from '@/types/database'
import { usePermissions } from '@/lib/usePermissions'
import { useKeyboardOpen } from '@/hooks/useVisualViewportInset'

type Tab = {
  to: string
  label: string
  icon: typeof Home
  /** Module key for the route gate. Home has none — always shown. */
  module?: string
  /** Shown only in a slot the tabs above it leave free (see barTabs). */
  spare?: true
  /** Shown only to these roles (FM11 UF-06: workers and scouts). Others never see it. */
  roles?: readonly UserRole[]
}

/** Width in px of the sliding active-tab bar. */
const INDICATOR_W = 24

/** The most tabs a bar holds — five thumb-sized slots on a 360px phone. */
const BAR_SLOTS = 5

// Tab sets mirror the module keys used by gPack/gFarm in App.tsx so the
// `hasModule` filter matches the route gate exactly. Icons and labels match the
// Home tiles (modules.ts) so a tab reads as the tile it opens.
const PACK_TABS: Tab[] = [
  { to: '/',         label: 'Home',     icon: Home },
  { to: '/tipping',  label: 'Tipping',  icon: Layers,         module: 'tipping' },
  { to: '/pallets',  label: 'Pallets',  icon: Archive,        module: 'pallets' },
  { to: '/qc',       label: 'QC',       icon: FlaskConical,   module: 'qc' },
  { to: '/dispatch', label: 'Dispatch', icon: Send,           module: 'dispatch' },
  // A free slot only: INSPECTOR and QC_TECHNICIAN open neither Tipping nor
  // Dispatch (DEFAULT_MODULES_BY_ROLE, modules.ts), so theirs had two slots empty.
  { to: '/inspection', label: 'Inspection', icon: Shield,       module: 'inspection', spare: true },
]

// Every tab a role-specific packhouse bar can draw from, by module key.
const PACK_CATALOG: Record<string, Tab> = {
  ...Object.fromEntries(PACK_TABS.filter(t => t.module).map(t => [t.module!, t])),
  deliveries:   { to: '/deliveries',   label: 'Deliveries',   icon: Truck,       module: 'deliveries' },
  runs:         { to: '/runs',         label: 'Runs',         icon: Package,     module: 'runs' },
  coldroom:     { to: '/coldroom',     label: 'Coldroom',     icon: Snowflake,   module: 'coldroom' },
  loading_bay:  { to: '/loading-bay',  label: 'Floor Map',    icon: Grid3x3,     module: 'loading_bay' },
  consignments: { to: '/consignments', label: 'Consignments', icon: ShieldCheck, module: 'consignments' },
}

// Packhouse floor roles: their own pages in work order (Home is always first).
// Any role not listed here (ADMIN, MANAGER, SUPER_ADMIN, ...) keeps PACK_TABS.
const PACK_ROLE_MODULES: Partial<Record<UserRole, readonly string[]>> = {
  SCALE_OPERATOR: ['tipping', 'deliveries', 'runs', 'pallets'],
  LOADER:         ['dispatch', 'loading_bay', 'coldroom', 'pallets'],
  DISPATCHER:     ['dispatch', 'consignments', 'loading_bay', 'pallets'],
  QC_TECHNICIAN:  ['qc', 'pallets', 'runs', 'inspection'],
  INSPECTOR:      ['inspection', 'qc', 'pallets', 'consignments'],
}

/**
 * The packhouse tab list for a role: Home, the role's own tabs, then the
 * default tabs it lacks as SPARES. barTabs seats spares only in slots the
 * viewer's module grants leave free, so a bar is never emptier than the default.
 */
export function packTabsForRole(role: UserRole | undefined): Tab[] {
  const mods = role && PACK_ROLE_MODULES[role]
  if (!mods) return PACK_TABS
  const own = mods.map(m => ({ ...PACK_CATALOG[m], spare: undefined }))
  const have = new Set(own.map(t => t.to))
  const fill = PACK_TABS.filter(t => t.module && !have.has(t.to)).map(t => ({ ...t, spare: true as const }))
  return [PACK_TABS[0], ...own, ...fill]
}

const FIELD_ROLES: readonly UserRole[] = ['FARM_WORKER', 'FARM_SCOUT']

const FARM_TABS: Tab[] = [
  { to: '/',                   label: 'Home',     icon: Home },
  { to: '/farm/scouting',      label: 'Scouting', icon: Bug,      module: 'scouting' },
  { to: '/farm/sprays',        label: 'Sprays',   icon: SprayCan, module: 'sprays' },
  { to: '/farm/harvest-picks', label: 'Picks',    icon: Sprout,   module: 'harvest_picks' },
  // Free slots of a worker's and a scout's bar only (FM11 UF-06): a worker's
  // only other tab was Home, so their bar was hidden; a scout had Home ·
  // Scouting. Foreman, manager and admin bars stay as they were: their fifth
  // slot is earmarked for Loads (FM03 UF-19), so Map and Problems never take it.
  { to: '/farm/map',           label: 'Map',      icon: MapPin,        module: 'farm_map',      spare: true, roles: FIELD_ROLES },
  { to: '/farm/problems',      label: 'Problems', icon: AlertTriangle, module: 'farm_problems', spare: true, roles: FIELD_ROLES },
  // FM03 UF-19: the fifth slot for whoever opens Loads (foremen, managers,
  // admins by default) — the wagon they sent, its answer, a fix.
  { to: '/farm/loads',         label: 'Loads',    icon: Truck,         module: 'farm_loads',    spare: true },
]

const TRANSPORTER_TABS: Tab[] = [
  { to: '/',                         label: 'Home',         icon: Home },
  { to: '/transporter/trips',        label: 'Trips',        icon: Truck,        module: 'transporter_trips' },
  { to: '/transporter/availability', label: 'Availability', icon: CalendarDays, module: 'transporter_availability' },
  { to: '/transporter/fleet',        label: 'Fleet',        icon: Car,          module: 'transporter_fleet' },
]

// PACKHOUSE, FARM and TRANSPORTER get a bar; every other kind resolves to
// undefined → none.
const TAB_SETS: Partial<Record<TenantKind, Tab[]>> = {
  PACKHOUSE:   PACK_TABS,
  FARM:        FARM_TABS,
  TRANSPORTER: TRANSPORTER_TABS,
}

export function tabsForKind(kind: TenantKind | undefined, role?: UserRole): Tab[] {
  if (kind === 'PACKHOUSE') return packTabsForRole(role)
  return (kind && TAB_SETS[kind]) || []
}

// Routes that fill the screen and are not app pages — no bar, and no
// reservation (the body class goes with it, so the page gets its 44px back).
const NO_BAR_ROUTES = ['/clock-face', '/monitor']

export function bottomNavHiddenOn(pathname: string): boolean {
  return NO_BAR_ROUTES.some(r => pathname === r || pathname.startsWith(`${r}/`))
}

/**
 * The tabs a bar shows: every regular tab the viewer may open, then spare tabs
 * into whatever slots are left. A spare never displaces a regular tab, so
 * anyone whose five slots are full sees exactly the bar they had.
 */
export function barTabs<T extends Pick<Tab, 'spare'>>(tabs: readonly T[], canSee: (t: T) => boolean): T[] {
  const regular = tabs.filter(t => !t.spare && canSee(t))
  const spare = tabs.filter(t => t.spare && canSee(t))
  return [...regular, ...spare.slice(0, Math.max(0, BAR_SLOTS - regular.length))]
}

export function canSeeTab(
  tab: Tab,
  me: Parameters<typeof hasModule>[0],
  tenant: ActiveTenant | undefined,
  perms: Parameters<typeof hasModule>[3],
): boolean {
  if (tab.roles && !(me && tab.roles.includes(me.role))) return false
  if (!tab.module) return true   // Home — no gate
  const { enabledModules = null, slug } = tenant ?? {}
  return hasModule(me, tab.module, enabledModules, perms, slug)
}

// md:max-w-[8rem] + auto margins on the first and last tab: from md up the tabs
// stay thumb-sized and sit together in the middle of the bar, instead of four
// tabs spread over 1024px (256px each, the icon lost in the middle — X-07).
// (-of-type, not :first/:last: the indicator <span> is the nav's first child.)
// The bar's background still spans the width. Done on the tab, not the <nav>,
// so the nav's own class list stays as it is.
function tabClass({ isActive }: { isActive: boolean }): string {
  return clsx(
    'flex-1 md:max-w-[8rem] md:first-of-type:ml-auto md:last-of-type:mr-auto flex flex-col items-center justify-center gap-0.5 min-h-[44px] text-[10px] font-medium pressable',
    isActive ? 'text-green-400' : 'text-fg-muted hover:text-gray-200 light:hover:text-fg',
  )
}

// The sliding active-tab bar. It is a LEAF positioned by `transform` on itself:
// the nav stays transform-free (fixed element, iOS footgun below). x is null
// when no tab is active (a page that is not on the bar) -> hidden.
function useActiveTabIndicator(navRef: RefObject<HTMLElement>, deps: unknown[]) {
  const [x, setX] = useState<number | null>(null)
  // Slide only between two placements we have actually shown; the first one,
  // and one after being hidden, appear in place instead of sweeping from x=0.
  const [slide, setSlide] = useState(false)
  const measure = () => {
    const a = navRef.current?.querySelector<HTMLElement>('a[aria-current="page"]')
    setX(a ? a.offsetLeft + a.offsetWidth / 2 - INDICATOR_W / 2 : null)
  }
  // Deps include `show`: the nav is display:none until then, so offsets read 0.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(measure, deps)
  // Tabs are centred and capped from md up, so a resize or rotation moves them.
  useEffect(() => {
    window.addEventListener('resize', measure)
    window.addEventListener('orientationchange', measure)
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('orientationchange', measure)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => { setSlide(x !== null) }, [x])
  return { x, slide }
}

export default function BottomNav() {
  const { data: me } = useCurrentUser()
  const { data: tenant } = useActiveTenant()
  const { perms } = usePermissions()
  const { pathname } = useLocation()

  const visible = barTabs(tabsForKind(tenant?.kind, me?.role), t => canSeeTab(t, me, tenant, perms))
  // A bar with only Home is pointless — needs at least one real destination.
  const show = visible.length > 1 && !bottomNavHiddenOn(pathname)

  // Reserve matching space (index.css gates the body padding by pointer:coarse,
  // so this class is a no-op on mouse/desktop where the bar is `hidden`).
  useEffect(() => {
    document.body.classList.toggle('has-bottom-nav', show)
    return () => document.body.classList.remove('has-bottom-nav')
  }, [show])

  // Stand aside while an on-screen keyboard is up (farm audit X-01 / W-12).
  // With `interactive-widget=resizes-content` the layout shrinks to the room
  // above the keys, so this `bottom-0` bar rode up with them and took 45px of
  // the ~475 left — over the field being typed in. `invisible`, not `hidden`:
  // no display flip on a fixed element (the iOS footgun below), and the body's
  // reservation stays put, so nothing reflows under the thumb mid-word.
  const keyboardUp = useKeyboardOpen()

  const navRef = useRef<HTMLElement>(null)
  const { x: indX, slide } = useActiveTabIndicator(navRef, [pathname, visible.length, show])

  // Keep the bar MOUNTED even when hidden (don't `return null`): iOS standalone
  // Safari mis-positions a `position: fixed` element that's ADDED to the DOM after
  // first paint until a scroll repaints it. Always rendering it avoids that
  // dynamic-add case. (`transform-gpu` was tried here too but a transform on a
  // fixed element is itself an iOS positioning footgun — backed out 2026-06-30.)
  // `hidden` always; `coarse:flex` only when there are ≥2 destinations to show
  // (touch devices), so non-touch/desktop and tenants without a bar render nothing.
  return (
    <nav
      className={`hidden ${show ? 'coarse:flex' : ''}${keyboardUp ? ' invisible' : ''} fixed bottom-0 inset-x-0 z-chrome bg-canvas/95 backdrop-blur border-t hairline pb-[env(safe-area-inset-bottom)] print:hidden`}
      ref={navRef}
      aria-label="Primary">
      <span
        aria-hidden="true"
        data-testid="bottom-nav-indicator"
        className={clsx(
          'pointer-events-none absolute top-0 left-0 h-0.5 rounded-full bg-green-400',
          slide && 'motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-out',
          indX === null && 'opacity-0',
        )}
        style={{ width: INDICATOR_W, transform: `translateX(${indX ?? 0}px)` }}
      />
      {visible.map(t => (
        // GuardedNavLink, not NavLink: this bar sits under the operator's
        // thumb throughout a capture, so it is the likeliest way to lose one.
        <GuardedNavLink key={t.to} to={t.to} end={t.to === '/'} className={tabClass}>
          <t.icon size={20} />
          {t.label}
        </GuardedNavLink>
      ))}
    </nav>
  )
}
